import { OrientedBoundingBox } from "@/components/r3f/utils/oriented-bounding-box";
import { LodPointCloud, LodTreeNode } from "@faro-lotv/lotv";
import { Box3, Matrix4, Quaternion, Vector3 } from "three";

/** Points sampled evenly from a LOD point cloud */
export type LodPointsSample = {
  /** World positions relative to origin, as x, y, z triplets */
  positions: Float32Array;
  /** World position the positions are relative to, so the Float32 values keep millimeter precision */
  origin: Vector3;
  /** Deepest LOD level included: all the points of the levels up to it are in the sample */
  depth: number;
  /** Number of sampled points after each level from the root, the points of a level follow those of the levels above */
  levelEnds: number[];
  /** Number of downloaded LOD nodes */
  nodeCount: number;
  /** Scan index of each point, as used by the scanIds attribute, if requested and all the points have one */
  scanIndices?: Uint16Array;
};

export type SampleLodPointsOptions = {
  /** World region to sample, the whole point cloud if undefined */
  region?: OrientedBoundingBox;
  /** Upper bound of the points to download, the sample stops at the deepest LOD level within this budget */
  maxPoints: number;
  /** Indices of the scans to leave out, as used by the scanIds attribute of the points */
  hiddenScanIndices?: ReadonlySet<number>;
  /** True to return the scan index of each point */
  keepScanIndices?: boolean;
  /** Number of nodes downloaded in parallel */
  maxParallelDownloads?: number;
  /** Called after each downloaded node, with the number of points sampled so far */
  onProgress?(sampledPoints: number): void;
  /** Aborts the pending downloads */
  signal?: AbortSignal;
};

const DEFAULT_PARALLEL_DOWNLOADS = 6;

/**
 * Samples a point cloud independently from what is on screen, by downloading whole LOD levels,
 * so the density is uniform over the region. Useful to analyze the data on the CPU (e.g. floors, areas).
 *
 * @param pointCloud The point cloud to sample
 * @param options How to sample
 * @returns the sampled points
 */
export async function sampleLodPoints(
  pointCloud: LodPointCloud,
  {
    region,
    maxPoints,
    hiddenScanIndices,
    keepScanIndices,
    maxParallelDownloads = DEFAULT_PARALLEL_DOWNLOADS,
    onProgress,
    signal,
  }: SampleLodPointsOptions,
): Promise<LodPointsSample> {
  pointCloud.updateMatrixWorld();
  const { tree } = pointCloud;
  const localToWorld = pointCloud.matrixWorld.clone();

  const regionMatrix = region
    ? new Matrix4().compose(
        new Vector3().fromArray(region.position),
        new Quaternion().fromArray(region.rotation),
        new Vector3().fromArray(region.size),
      )
    : undefined;
  const origin = regionMatrix
    ? new Vector3().setFromMatrixPosition(regionMatrix)
    : tree.root.boundingBox.getCenter(new Vector3()).applyMatrix4(localToWorld);
  // Maps the points of the tree to the unit box of the region, where inside means |x|, |y|, |z| <= 0.5
  const localToRegion = regionMatrix
    ? regionMatrix.clone().invert().multiply(localToWorld)
    : undefined;
  const regionInTree = regionMatrix
    ? new Box3(new Vector3(-0.5, -0.5, -0.5), new Vector3(0.5, 0.5, 0.5))
        .applyMatrix4(regionMatrix)
        .applyMatrix4(localToWorld.clone().invert())
    : undefined;
  function intersects(node: LodTreeNode): boolean {
    return !regionInTree || node.boundingBox.intersectsBox(regionInTree);
  }

  let positions = new Float32Array(0);
  let scanIndices = keepScanIndices ? new Uint16Array(0) : undefined;
  let count = 0;
  let depth = -1;
  let nodeCount = 0;
  const point = new Vector3();
  const inRegion = new Vector3();

  function addPoints(local: Float32Array, scanIds?: Uint16Array): void {
    if (!scanIds) scanIndices = undefined;
    for (let i = 0; i < local.length / 3; i++) {
      if (scanIds && hiddenScanIndices?.has(scanIds[i])) continue;
      point.fromArray(local, i * 3);
      if (localToRegion) {
        inRegion.copy(point).applyMatrix4(localToRegion);
        if (
          Math.abs(inRegion.x) > 0.5 ||
          Math.abs(inRegion.y) > 0.5 ||
          Math.abs(inRegion.z) > 0.5
        ) {
          continue;
        }
      }
      point.applyMatrix4(localToWorld).sub(origin);
      point.toArray(positions, count * 3);
      if (scanIndices && scanIds) scanIndices[count] = scanIds[i];
      count++;
    }
  }

  // The hierarchy of large trees is loaded while downloading, so the levels are discovered one at a time
  async function downloadLevel(nodes: LodTreeNode[]): Promise<void> {
    const queue = [...nodes];
    async function downloadNext(): Promise<void> {
      for (let node = queue.shift(); node; node = queue.shift()) {
        signal?.throwIfAborted();
        const nodeFetch = await tree.getNodePoints(node.id);
        function abort(): void {
          nodeFetch.abort();
        }
        signal?.addEventListener("abort", abort);
        try {
          const chunk = await nodeFetch.points();
          addPoints(chunk.positions, chunk.scanIds);
        } finally {
          signal?.removeEventListener("abort", abort);
        }
        nodeCount++;
        onProgress?.(count);
      }
    }
    await Promise.all(
      Array.from({ length: maxParallelDownloads }, downloadNext),
    );
  }

  let level = [tree.root].filter(intersects);
  let totalPoints = 0;
  const levelEnds: number[] = [];
  while (level.length) {
    const levelPoints = level.reduce((sum, node) => sum + node.numPoints, 0);
    if (depth >= 0 && totalPoints + levelPoints > maxPoints) break;
    totalPoints += levelPoints;

    const grown = new Float32Array(totalPoints * 3);
    grown.set(positions.subarray(0, count * 3));
    positions = grown;
    if (scanIndices) {
      const grownIndices = new Uint16Array(totalPoints);
      grownIndices.set(scanIndices.subarray(0, count));
      scanIndices = grownIndices;
    }

    await downloadLevel(level);
    [{ depth }] = level;
    levelEnds.push(count);
    level = level.flatMap((node) => node.children).filter(intersects);
  }

  return {
    positions: positions.slice(0, count * 3),
    origin,
    depth,
    levelEnds,
    nodeCount,
    scanIndices: scanIndices?.slice(0, count),
  };
}
